import React, { useState, useEffect, useCallback, useContext, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaEye, FaEdit, FaTrashAlt } from 'react-icons/fa';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';

import styles from './ListaRateios.module.css';
import styles_global from '../css/global.module.css';
import styles_content from '../css/main-content.module.css';

import { UserContext } from '../../UserContext';
import Pagination from '../pagination/Pagination';
import { useHttpClient } from '../hooks/useHttpClient';
import { useNotificacoes } from '../hooks/useNotificacoes';
import { formatDocumento } from '../../util/validacoes';

function ListarRateios() {
    const { user } = useContext(UserContext);
    const idEmpresa = user?.empresa?.id;
    const { get, del } = useHttpClient();
    const { exibirErro } = useNotificacoes();
    const navigate = useNavigate();

    const [usinas, setUsinas] = useState([]);
    const [rateios, setRateios] = useState([]);
    const [clientes, setClientes] = useState([]);
    const [filtros, setFiltros] = useState({ idEmpresa: idEmpresa || '', idUsina: '', idCliente: '', nomeUnidade: '' });
    const [currentPage, setCurrentPage] = useState(0);
    const pageSize = 10;
    const [totalPages, setTotalPages] = useState(0);
    const [loading, setLoading] = useState(false);
    const buscou = useRef(false);

    // Carrega usinas apenas uma vez no mount
    useEffect(() => {
        if (!idEmpresa) return;
        (async () => {
            try {
                const data = await get(`/hello-sol/usinas/buscar?idEmpresa=${idEmpresa}`);
                setUsinas(Array.isArray(data) ? data : []);
            } catch {
                setUsinas([]);
            }
        })();
    }, []); // <— executa só no mount


    const fetchRateios = useCallback(async (page = 0) => {
        if (!filtros.idUsina) return;
        setLoading(true);
        try {
            const params = new URLSearchParams({
                idEmpresa: filtros.idEmpresa,
                idUsina: filtros.idUsina,
                idCliente: filtros.idCliente,
                nomeUnidade: filtros.nomeUnidade,
                page: page.toString(),
                pageSize: pageSize.toString(),
            }).toString();
            const data = await get(`/hello-sol/rateios?${params}`);
            setRateios(data.content || []);
            setTotalPages(data.totalPages || 0);
            setCurrentPage(page);
            buscou.current = true;
        } catch {
            setRateios([]);
            buscou.current = true;
        } finally {
            setLoading(false);
        }
    }, [filtros, get]);

    // Carregar clientes ao mudar a usina
    useEffect(() => {
        if (!filtros.idUsina) {
            setClientes([]);
            return;
        }

        (async () => {
            try {
                const resp = await get(`/hello-sol/usuarios/buscarUsuarioUsina?` +
                    new URLSearchParams({
                        idUsina: filtros.idUsina,
                        idTipoUsuario: 3 // Cliente
                    }).toString());
                setClientes(Array.isArray(resp) ? resp : (resp.content ?? []));
            } catch {
                exibirErro('Erro ao buscar clientes da usina.');
            }
        })();
    }, [filtros.idUsina]);

    const handleChange = e => {
        const { name, value } = e.target;
        setFiltros(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = e => {
        e.preventDefault();
        if (!filtros.idUsina) {
            exibirErro('Selecione uma usina para buscar os rateios.');
            return;
        }
        fetchRateios(0);
    };

    const confirmDelete = id => {
        confirmAlert({
            title: 'Confirmação',
            message: 'Deseja realmente excluir este rateio?',
            buttons: [
                {
                    label: 'Sim',
                    onClick: async () => {
                        try {
                            await del(`/hello-sol/rateios/${id}`);
                            fetchRateios(currentPage);
                        } catch (err) {
                            exibirErro(err.message);
                        }
                    }
                },
                { label: 'Não' }
            ],
        });
    };

    const handlePageChange = page => {
        if (page >= 0 && page < totalPages) {
            fetchRateios(page);
        }
    };

    return (
        <div className={styles_global.container}>
            <h2>Consultar Rateios</h2>

            <form className={styles.filterForm} onSubmit={handleSubmit}>
                <div className={styles_content.formField}>
                    <label htmlFor="idUsina">Usina</label>
                    <select
                        id="idUsina"
                        name="idUsina"
                        value={filtros.idUsina}
                        onChange={handleChange}
                        className={styles.campoUsina}
                    >
                        <option value="">Selecione a Usina</option>
                        {usinas.map(u => (
                            <option key={u.id} value={u.id}>{u.nome}</option>
                        ))}
                    </select>
                </div>

                <div className={styles_content.formField}>
                    <label>Cliente</label>
                    <select name="idCliente" value={filtros.idCliente} onChange={handleChange}
                            className={styles.campoCliente}
                    >
                        <option value="">Todos os Clientes</option>
                        {clientes.map(cliente => (
                            <option key={cliente.id} value={cliente.id}>
                                {cliente.nome}
                            </option>
                        ))}
                    </select>
                </div>

                <div className={styles_content.formField}>
                    <label>Nome da Unidade</label>
                    <input
                        name="nomeUnidade"
                        value={filtros.nomeUnidade}
                        onChange={handleChange}
                        placeholder="Ex: Bloco A"
                        className={styles.campoNome}

                    />
                </div>

                <div className={styles.botoesAbaixo}>
                    <button type="submit" disabled={loading}>
                        {loading ? 'Consultando...' : 'Consultar'}
                    </button>
                    <button type="button" onClick={() => navigate('/home/cadastrar-rateio')}>
                        Novo Rateio
                    </button>
                </div>
            </form>

            {buscou.current && (
                loading ? (
                    <p className={styles_global.loading}>Carregando...</p>
                ) : rateios.length > 0 ? (
                    <>
                        <div className={styles.tableContainer}>
                            <table className={styles.tableRateio}>
                                <thead>
                                <tr>
                                    <th>CPF/CNPJ</th>
                                    <th>Nome CLIENTE</th>
                                    <th>Unidade</th>
                                    <th>Percentual</th>
                                    <th>Ações</th>
                                </tr>
                                </thead>
                                <tbody>
                                {rateios.map((r, i) => (
                                    <tr key={r.id} className={i % 2 === 0 ? 'even-row' : 'odd-row'}>
                                        <td>{formatDocumento(r.cliente?.cpfCnpj)}</td>
                                        <td>{r.cliente?.nome}</td>
                                        <td>{r.unidadeConsumidora?.nomeUnidade}</td>
                                        <td>{r.percentual}%</td>
                                        <td className={styles.actions}>
                                            <Link to={`/home/detalhar-rateio/${r.id}`} className={styles.iconButton}>
                                                <FaEye title="Visualizar" className={styles.icon}/>
                                            </Link>
                                            <Link to={`/home/cadastrar-rateio/${r.id}`} className={styles.iconButton}>
                                                <FaEdit title="Editar" className={styles.icon}/>
                                            </Link>
                                            <Link to="#" onClick={() => confirmDelete(r.id)}
                                                  className={styles.iconButton}>
                                                <FaTrashAlt title="Excluir"
                                                            className={`${styles.icon} ${styles.delete}`}/>
                                            </Link>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={handlePageChange}
                        />
                    </>
                ) : (
                    <p className={styles_global.noData}>Nenhum rateio encontrado.</p>
                )
            )}
        </div>
    );
}

export default ListarRateios;
